/**
 * dataCache — Single canonical in-memory cache for ALL REST data.
 *
 * L1: In-process memory (instant, survives component re-renders)
 * L2: sessionStorage (survives client-side navigation, cleared on tab close)
 *
 * SWR strategy: hooks read L1 → L2 → fetch. Write always goes L1 + L2.
 *
 * API:
 *   get(key)              → value | undefined
 *   getFresh(key, ttlMs)  → value | undefined  (only if < ttlMs old)
 *   getStale(key, maxAge) → value | undefined  (SWR: returns data up to maxAge ms old)
 *   set(key, value)       → void (writes L1 + L2)
 *   age(key)              → ms since last set (Infinity if never)
 *   clear()               → void (logout)
 */
const _store = {};
const _ts = {};

const SWR_PREFIX = 'sc:';
const SWR_MAX_AGE = 10 * 60 * 1000; // 10 min max staleness for SWR

function _writeL2(key, value, ts) {
  try {
    sessionStorage.setItem(SWR_PREFIX + key, JSON.stringify({ v: value, t: ts }));
  } catch { /* quota exceeded — silent */ }
}

function _readL2(key) {
  try {
    const raw = sessionStorage.getItem(SWR_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function get(key) {
  if (key in _store) return _store[key];
  // L2 fallback
  const l2 = _readL2(key);
  if (l2) {
    _store[key] = l2.v;
    _ts[key] = l2.t;
    return l2.v;
  }
  return undefined;
}

export function getFresh(key, ttlMs) {
  const t = _ts[key];
  if (t !== undefined && Date.now() - t <= ttlMs) return _store[key];
  // Check L2
  const l2 = _readL2(key);
  if (l2 && Date.now() - l2.t <= ttlMs) {
    _store[key] = l2.v;
    _ts[key] = l2.t;
    return l2.v;
  }
  return undefined;
}

/**
 * SWR read: returns data even if stale (up to maxAge), so hooks can
 * show instantly while background revalidation happens.
 */
export function getStale(key, maxAge = SWR_MAX_AGE) {
  const t = _ts[key];
  if (t !== undefined && Date.now() - t <= maxAge) return _store[key];
  // Check L2
  const l2 = _readL2(key);
  if (l2 && Date.now() - l2.t <= maxAge) {
    _store[key] = l2.v;
    _ts[key] = l2.t;
    return l2.v;
  }
  return undefined;
}

export function set(key, value) {
  _store[key] = value;
  _ts[key] = Date.now();
  _writeL2(key, value, _ts[key]);
}

export function age(key) {
  const t = _ts[key];
  if (t !== undefined) return Date.now() - t;
  const l2 = _readL2(key);
  if (l2) return Date.now() - l2.t;
  return Infinity;
}

export function has(key) {
  if (key in _store) return true;
  return !!_readL2(key);
}

export function clear() {
  for (const k of Object.keys(_store)) delete _store[k];
  for (const k of Object.keys(_ts)) delete _ts[k];
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(SWR_PREFIX)) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => sessionStorage.removeItem(k));
  } catch {}
}
